import type {
  LeadGroup,
  NextStepItem,
  ScheduleItem,
  SidekickUpdateItem,
  SignalItem,
} from "../contacts.types";
import styled from "@emotion/styled";
import { InfoCard, Typography, Skeleton } from "@avenue-8/sidekick-ui";
import {
  LeadAccordion,
  LeadAccordionDetails,
  LeadAccordionSummary,
} from "./LeadAccordion";
import { FormattedDate } from "@shared/components/FormattedDate";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  InteractionDetailResponseDto,
  useGetInteraction,
} from "../../../shared/generated/crm";

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration-line: underline;
  text-decoration-style: dotted;

  &:hover {
    text-decoration-style: solid;
  }
`;

const PendingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid ${(p) => p.theme.colors.border2};
  background-color: ${(p) => p.theme.colors.background2};
  border-radius: 0.5rem;
  color: ${(p) => p.theme.text.colors.secondary};
  font-size: ${(p) => p.theme.text.fontSizes.s};
`;

export const ItemsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    margin-bottom: 0.25rem;
  }
`;

const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(p) => p.theme.text.colors.secondary};
  font-size: ${(p) => p.theme.text.fontSizes.s};
  white-space: nowrap;
  flex-shrink: 0;
`;

type SidekickUpdatesCardProps = {
  title: string;
  content: SidekickUpdateItem[];
  leadGroups?: LeadGroup[];
  hasPendingSuggestions?: boolean;
};

const parseInteractionSourceLink = (
  interaction?: InteractionDetailResponseDto,
): string | null => {
  if (!interaction?.channel) return null;
  const {
    channel: { type, provider, providerId },
  } = interaction;
  if (type === "email" && provider === "gmail" && providerId) {
    return `https://mail.google.com/mail/u/0/#inbox/${providerId}`;
  }
  return null;
};

const SignalContentItem = ({ item }: { item: SignalItem }) => {
  const shouldFetchInteraction = !!(
    item.source?.startsWith("email:") && item.interactionId
  );
  const interactionDetails = useGetInteraction(
    item.interactionId ?? "",
    undefined,
    { query: { enabled: shouldFetchInteraction } },
  );
  const interactionSourceLink = parseInteractionSourceLink(
    interactionDetails.data,
  );
  const { occurrenceDate, sourceLabel } = item;
  const formattedSource = interactionSourceLink ? (
    <SourceLink
      href={interactionSourceLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      {sourceLabel}
      <OpenInNewIcon fontSize="inherit" />
    </SourceLink>
  ) : (
    sourceLabel
  );

  return (
    <ItemBody>
      <LabelRow>
        <Typography variant="subtitle2">{item.label}</Typography>
        {(occurrenceDate || sourceLabel) && (
          <SourceBadge>
            {interactionDetails.isLoading ? (
              <Skeleton variant="text" width={100} />
            ) : (
              formattedSource
            )}
            {occurrenceDate && (
              <>
                {" "}
                <BulletSeparator />
                <FormattedDate value={occurrenceDate} />
              </>
            )}
          </SourceBadge>
        )}
      </LabelRow>
      <Typography variant="body1">{item.text}</Typography>
    </ItemBody>
  );
};

const ScheduleContentItem = ({ item }: { item: ScheduleItem }) => (
  <ItemBody>
    <LabelRow>
      <Typography variant="subtitle2">{item.label}</Typography>
    </LabelRow>
    <Typography variant="body2" color="text.secondary">
      {item.dateText ?? (item.date ? <FormattedDate value={item.date} /> : "")}
      {item.time && ` at ${item.time}`}
      {item.location && ` · ${item.location}`}
    </Typography>
  </ItemBody>
);

const NextStepContentItem = ({ item }: { item: NextStepItem }) => (
  <ItemBody>
    <LabelRow>
      <Typography variant="subtitle2">Next step</Typography>
    </LabelRow>
    <Typography variant="body2" color="text.secondary">
      {item.description}
      {item.dueAt && (
        <>
          {" "}
          (due <FormattedDate value={item.dueAt} />)
        </>
      )}
    </Typography>
  </ItemBody>
);

const getItemKey = (item: SidekickUpdateItem, index: number): string => {
  if (item.kind === "signal" && item.interactionId)
    return `signal-${item.interactionId}-${item.label}-${item.occurrenceDate ?? ""}`;
  return `${item.kind}-${index}`;
};

export const ContentItem = ({ item }: { item: SidekickUpdateItem }) => {
  if (item.kind === "signal") return <SignalContentItem item={item} />;
  if (item.kind === "schedule") return <ScheduleContentItem item={item} />;
  return <NextStepContentItem item={item} />;
};

const LeadGroupAccordion = ({
  group,
  defaultExpanded,
}: {
  group: LeadGroup;
  defaultExpanded: boolean;
}) => (
  <LeadAccordion disableGutters defaultExpanded={defaultExpanded}>
    <LeadAccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
      <Typography variant="subtitle1">
        {group.label}
        {group.lastSignalAt && (
          <>
            <BulletSeparator />
            <FormattedDate value={group.lastSignalAt} />
          </>
        )}
      </Typography>
    </LeadAccordionSummary>
    <LeadAccordionDetails>
      <ItemsStack>
        {group.content.map((item, i) => (
          <ContentItem key={getItemKey(item, i)} item={item} />
        ))}
      </ItemsStack>
    </LeadAccordionDetails>
  </LeadAccordion>
);

export const SidekickUpdatesCard = ({
  title,
  content,
  leadGroups,
  hasPendingSuggestions,
}: SidekickUpdatesCardProps) => (
  <InfoCard title={title}>
    {leadGroups && leadGroups.length > 0 ? (
      leadGroups.map((group, i) => (
        <LeadGroupAccordion
          key={group.id}
          group={group}
          defaultExpanded={i === 0}
        />
      ))
    ) : (
      <ItemsStack>
        {content.map((item, i) => (
          <ContentItem key={getItemKey(item, i)} item={item} />
        ))}
      </ItemsStack>
    )}
    {hasPendingSuggestions && (
      <PendingBar>
        <InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
        <Typography variant="caption">
          Updates pending review in contact fields
        </Typography>
      </PendingBar>
    )}
  </InfoCard>
);
